/**
 * NiV "שימוש ומדדים" tab — product telemetry dashboard (sessions, page views,
 * feature usage) plus a per-clinic usage table.
 *
 * Sections:
 * 0. Public pages traffic (NivPublicTrafficSection) — views / uniques /
 *    referrers / lead insights for /, /start, /for/*, blog (niv_public_pageviews)
 * 1. KPI row — active clinics today/7d/30d, sessions, device split, PWA
 * 1b. Progress-by-day grid (NivProgressSection) — SMS / AI / Lenny / video /
 *     patients / appointments / treatments / DAU / business, from
 *     niv_system_daily + the overview series
 * 2. DAU chart — active therapists per day
 * 3. Top pages + device breakdown charts
 * 4. Feature adoption matrix (clinics × features)
 * 5. Sortable per-clinic usage table (logins / meetings / integrations)
 *
 * Shared Hebrew label maps live in ./telemetryLabels.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  useGetTelemetryOverviewQuery,
  useGetClinicUsageQuery,
  useRecomputeTelemetryScoresMutation,
} from "../../../../store/services/nivApi";
import {
  Buildings,
  ChartLineUp,
  Devices,
  Lightning,
  CaretUp,
  CaretDown,
  SquaresFour,
  ChartBar,
  Info,
  ArrowsClockwise,
  Plug,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import { useToast } from "../../../../contexts/ToastContext";
import { formatDayKey, TelemetryTooltip } from "./telemetryChartUtils";
import {
  FEATURE_LABELS,
  DEVICE_LABELS,
  pageLabel,
  featureStateLabel,
  formatComputedAt,
  pendingUpdateTone,
} from "./telemetryLabels";
import NivProgressSection from "./NivProgressSection";
import NivPublicTrafficSection from "./NivPublicTrafficSection";
import "./NivTelemetryTab.css";

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <div className="ntl-kpi">
    <div className="ntl-kpi-icon">
      <Icon size={18} />
    </div>
    <span className="ntl-kpi-label">{label}</span>
    <span className="ntl-kpi-value">{value}</span>
    {sub && <span className="ntl-kpi-sub">{sub}</span>}
  </div>
);

const FEATURE_KEYS = Object.keys(FEATURE_LABELS);

/** Actions tracked on the mobile "install CliniKit" banner (telemetry.trackPwaInstallPrompt). */
const PWA_INSTALL_ACTIONS = [
  { key: "shown", label: "הוצג" },
  { key: "install", label: "לחיצה על התקנה" },
  { key: "installed", label: "הותקן" },
  { key: "declined", label: "סירוב בדיאלוג" },
  { key: "steps", label: "פתיחת הוראות" },
  { key: "copyLink", label: "העתקת קישור" },
  { key: "dismiss", label: "סגירה / אחר כך" },
  { key: "never", label: "לא להציג שוב" },
];

// ============================================
// USAGE TABLE CONFIG
// ============================================

const USAGE_COLUMNS = [
  { key: "clinicName", label: "קליניקה", isText: true },
  { key: "daysSinceLastLogin", label: "כניסה אחרונה" },
  { key: "sessions30", label: "כניסות 30 יום" },
  { key: "meetingsPast30", label: "פגישות 30 יום" },
  { key: "meetingsCompleted30", label: "מתוכן הושלמו" },
  { key: "meetingsNext7", label: "פגישות קרובות 7" },
  { key: "integrations", label: "אינטגרציות" },
  { key: "pendingUpdateCount", label: "ממתינות לעדכון" },
];

/** Inactivity tone for the "last login" cell (mirrors the health cards). */
const loginTone = (days) => {
  if (days == null) return "danger";
  const n = Number(days);
  if (!Number.isFinite(n)) return null;
  if (n >= 30) return "danger";
  if (n >= 7) return "warn";
  return "ok";
};

// Feature-matrix cell intensity from 30-day usage count (+ connected-idle state)
const cellIntensity = (count, state) => {
  const n = Number(count) || 0;
  if (n <= 0) return state === "connected_idle" ? "idle" : "none";
  if (n < 5) return "low";
  return "high";
};

// ============================================
// MAIN COMPONENT
// ============================================

function NivTelemetryTab({ isAuthorized }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [sort, setSort] = useState({ key: "daysSinceLastLogin", dir: "desc" });
  const [recompute, { isLoading: recomputing }] = useRecomputeTelemetryScoresMutation();

  const handleRecomputeAll = async () => {
    try {
      const res = await recompute({ all: true }).unwrap();
      showSuccess(`הנתונים חושבו מחדש (${res?.written ?? 0} קליניקות)`);
    } catch {
      showError("שגיאה בחישוב מחדש של הנתונים");
    }
  };

  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
  } = useGetTelemetryOverviewQuery({ days: 30 }, { skip: !isAuthorized });
  const {
    data: usageData,
    isLoading: scoresLoading,
    isError: scoresError,
  } = useGetClinicUsageQuery(undefined, { skip: !isAuthorized });

  // Flattened rows: the nested usage doc plus the derived columns the table
  // sorts on, so sorting never has to walk the nested maps.
  const scores = useMemo(() => {
    const list = Array.isArray(usageData) ? usageData : [];
    return list.map((u) => ({
      ...u,
      daysSinceLastLogin: u?.logins?.daysSinceLastLogin ?? null,
      sessions30: u?.logins?.sessions30d ?? 0,
      meetingsPast30: u?.meetings?.past30d?.total ?? 0,
      meetingsCompleted30: u?.meetings?.past30d?.completed ?? 0,
      meetingsNext7: u?.meetings?.next7d?.total ?? 0,
      integrations: u?.sortIntegrationsConnected ?? 0,
      pendingUpdateCount: u?.meetings?.pendingUpdateCount ?? 0,
    }));
  }, [usageData]);
  const lastComputedAt = useMemo(() => {
    let latest = null;
    for (const s of scores) {
      const v = s?.computedAt;
      const ms = v?.seconds ? v.seconds * 1000 : v ? new Date(v).getTime() : NaN;
      if (Number.isFinite(ms) && (!latest || ms > latest)) latest = ms;
    }
    return latest ? formatComputedAt(latest) : null;
  }, [scores]);

  const stats = useMemo(() => {
    const dailySeries = overview?.dailySeries || [];
    const perClinic = overview?.perClinic || [];
    const deviceTotals = overview?.deviceTotals || {};
    const pageTotals = overview?.pageTotals || {};

    // --- Active clinics today / 7d / 30d ---
    const lastDay = dailySeries[dailySeries.length - 1];
    const activeToday = lastDay?.activeClinics || 0;
    // 7d: prefer accurate distinct count from score docs (daysSinceActive),
    // fall back to peak daily count from the series
    const active7dFromScores = scores.filter(
      (s) => Number(s?.daysSinceActive) <= 7 && Number.isFinite(Number(s?.daysSinceActive))
    ).length;
    const active7dFromSeries = dailySeries
      .slice(-7)
      .reduce((max, d) => Math.max(max, d?.activeClinics || 0), 0);
    const active7d = scores.length ? active7dFromScores : active7dFromSeries;
    const active30d = perClinic.length;

    // --- Device split ---
    const deviceSum =
      (deviceTotals.desktop || 0) +
      (deviceTotals.tablet || 0) +
      (deviceTotals.mobile || 0);
    const devicePct = (key) =>
      deviceSum ? Math.round(((deviceTotals[key] || 0) / deviceSum) * 100) : 0;
    const deviceSplitLabel = deviceSum
      ? `${devicePct("desktop")}% מחשב · ${devicePct("mobile")}% נייד · ${devicePct("tablet")}% טאבלט`
      : "אין נתונים";

    // --- DAU series ---
    const dauData = dailySeries.map((d) => ({
      day: formatDayKey(d?.dateKey),
      therapists: d?.activeTherapists || 0,
      clinics: d?.activeClinics || 0,
    }));

    // --- Top pages ---
    const topPages = Object.entries(pageTotals)
      .map(([key, count]) => ({ key, label: pageLabel(key), count: Number(count) || 0 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    const topPageMax = topPages[0]?.count || 1;

    // --- Devices chart data ---
    const deviceData = ["desktop", "mobile", "tablet"].map((key) => ({
      key,
      label: DEVICE_LABELS[key],
      count: deviceTotals[key] || 0,
    }));

    const totalSessions = overview?.totalSessions || 0;
    const pwaSessions = overview?.pwaSessions || 0;

    // "Install CliniKit" banner funnel (mobile browser → PWA)
    const pwaInstall = overview?.pwaInstallTotals || {};
    const pwaInstallShown = pwaInstall.shown || 0;
    const pwaInstallRows = PWA_INSTALL_ACTIONS.map((a) => ({
      key: a.key,
      label: a.label,
      count: pwaInstall[a.key] || 0,
    }));
    const pwaInstallMax = Math.max(1, ...pwaInstallRows.map((r) => r.count));

    return {
      pwaInstallShown,
      pwaInstallRows,
      pwaInstallMax,
      pwaInstallPct: (key) =>
        pwaInstallShown ? Math.round(((pwaInstall[key] || 0) / pwaInstallShown) * 100) : 0,
      activeToday,
      active7d,
      active30d,
      totalSessions,
      pwaSessions,
      pwaPct: totalSessions ? Math.round((pwaSessions / totalSessions) * 100) : 0,
      deviceSplitLabel,
      dauData,
      topPages,
      topPageMax,
      deviceData,
      hasDevices: deviceSum > 0,
    };
  }, [overview, scores]);

  // --- Sortable usage table ---
  const sortedScores = useMemo(() => {
    const col = USAGE_COLUMNS.find((c) => c.key === sort.key);
    const rows = [...scores];
    rows.sort((a, b) => {
      let result;
      if (col?.isText) {
        result = String(a?.[sort.key] || "").localeCompare(String(b?.[sort.key] || ""), "he");
      } else {
        result = (Number(a?.[sort.key]) || 0) - (Number(b?.[sort.key]) || 0);
      }
      return sort.dir === "asc" ? result : -result;
    });
    return rows;
  }, [scores, sort]);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" }
    );
  };

  // --- Feature matrix rows (most meetings first) ---
  const matrixRows = useMemo(
    () =>
      [...scores].sort(
        (a, b) => (Number(b?.meetingsPast30) || 0) - (Number(a?.meetingsPast30) || 0)
      ),
    [scores]
  );

  const goToClinic = (clinicId) => {
    if (clinicId) navigate(`/dashboard/niv/clinic/${clinicId}`);
  };

  if (overviewLoading || scoresLoading) {
    return (
      <ContentLoader message="טוען נתוני שימוש…" />
    );
  }

  if (overviewError && scoresError) {
    return (
      <div className="ntl-container">
        <div className="ntl-empty">לא הצלחנו לטעון את נתוני השימוש. נסו לרענן את העמוד.</div>
      </div>
    );
  }

  return (
    <div className="ntl-container">
      {/* Header */}
      <div className="ntl-header">
        <div>
          <h2 className="ntl-title">שימוש ומדדים</h2>
          <span className="ntl-note">
            <Info size={12} />
            טלמטריה נאספת מאז אוגוסט 2026 · אינטגרציות ומסמכים נספרים מהשרת
            {lastComputedAt && ` · חושב לאחרונה ${lastComputedAt}`}
          </span>
        </div>
        <button
          type="button"
          className="ntl-recompute-btn"
          onClick={handleRecomputeAll}
          disabled={recomputing}
          title="חישוב מחדש של כל הציונים (כולל שימוש מהיום)"
        >
          <ArrowsClockwise size={15} className={recomputing ? "ntl-spinner" : ""} />
          {recomputing ? "מחשב…" : "חשב מחדש"}
        </button>
      </div>

      {/* Public marketing pages: views / uniques / referrers / lead insights */}
      <NivPublicTrafficSection isAuthorized={isAuthorized} />

      {/* KPI row */}
      <div className="ntl-kpi-grid">
        <Kpi
          icon={Buildings}
          label="קליניקות פעילות היום"
          value={stats.activeToday}
          sub={`${stats.active7d} בשבוע האחרון · ${stats.active30d} ב-30 יום`}
        />
        <Kpi
          icon={ChartLineUp}
          label="כניסות (30 יום)"
          value={stats.totalSessions.toLocaleString("he-IL")}
        />
        <Kpi icon={Devices} label="פילוח מכשירים" value=" " sub={stats.deviceSplitLabel} />
        <Kpi
          icon={Lightning}
          label="כניסות מאפליקציה (PWA)"
          value={stats.pwaSessions.toLocaleString("he-IL")}
          sub={stats.totalSessions ? `${stats.pwaPct}% מהכניסות` : undefined}
        />
      </div>

      {/* Progress by day (system-wide daily snapshots) */}
      <NivProgressSection isAuthorized={isAuthorized} overview={overview} />

      {/* Charts */}
      <div className="ntl-charts-grid">
        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <ChartLineUp size={15} />
            מטפלים פעילים ביום (DAU)
          </h3>
          {stats.dauData.length === 0 ? (
            <div className="ntl-empty">אין עדיין נתוני שימוש</div>
          ) : (
            <div className="ntl-chart" dir="ltr">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={stats.dauData} margin={{ top: 12, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                  <XAxis
                    dataKey="day"
                    tickLine={false}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
                    width={28}
                  />
                  <Tooltip content={<TelemetryTooltip />} cursor={{ stroke: "var(--color-border)" }} />
                  <Area
                    type="monotone"
                    dataKey="therapists"
                    name="מטפלים פעילים"
                    stroke="var(--color-primary-dark)"
                    strokeWidth={2}
                    fill="var(--color-primary)"
                    fillOpacity={0.18}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <ChartBar size={15} />
            עמודים מובילים (30 יום)
          </h3>
          {stats.topPages.length === 0 ? (
            <div className="ntl-empty">אין עדיין נתוני שימוש</div>
          ) : (
            <div className="ntl-hbars">
              {stats.topPages.map((p) => (
                <div key={p.key} className="ntl-hbar-row">
                  <span className="ntl-hbar-label">{p.label}</span>
                  <div className="ntl-hbar-track">
                    <div
                      className="ntl-hbar-fill"
                      style={{ width: `${Math.max(4, (p.count / stats.topPageMax) * 100)}%` }}
                    />
                  </div>
                  <span className="ntl-hbar-value">{p.count.toLocaleString("he-IL")}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <Devices size={15} />
            פילוח מכשירים (30 יום)
          </h3>
          {!stats.hasDevices ? (
            <div className="ntl-empty">אין עדיין נתוני שימוש</div>
          ) : (
            <div className="ntl-chart" dir="ltr">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.deviceData} margin={{ top: 24, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                    interval={0}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
                    width={34}
                  />
                  <Tooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
                  <Bar dataKey="count" name="כניסות" fill="var(--color-primary-dark)" maxBarSize={40} radius={[4, 4, 0, 0]}>
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fontSize: 11, fontWeight: 600, fill: "var(--color-text-primary)" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <Lightning size={15} />
            באנר התקנת אפליקציה (30 יום)
          </h3>
          {stats.pwaInstallShown === 0 ? (
            <div className="ntl-empty">הבאנר עדיין לא הוצג לאף משתמש</div>
          ) : (
            <div className="ntl-hbars">
              {stats.pwaInstallRows.map((r) => (
                <div key={r.key} className="ntl-hbar-row">
                  <span className="ntl-hbar-label">{r.label}</span>
                  <div className="ntl-hbar-track">
                    <div
                      className="ntl-hbar-fill"
                      style={{ width: `${Math.max(4, (r.count / stats.pwaInstallMax) * 100)}%` }}
                    />
                  </div>
                  <span className="ntl-hbar-value">
                    {r.count.toLocaleString("he-IL")}
                    {r.key !== "shown" && r.count > 0 ? ` (${stats.pwaInstallPct(r.key)}%)` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

      </div>

      {/* Feature adoption matrix */}
      <section className="ntl-card">
        <h3 className="ntl-card-title">
          <SquaresFour size={15} />
          מטריצת אימוץ פיצ'רים (30 יום)
        </h3>
        {matrixRows.length === 0 ? (
          <div className="ntl-empty">אין עדיין נתוני ציונים</div>
        ) : (
          <>
            <div className="ntl-matrix-legend">
              <span className="ntl-matrix-legend-item">
                <span className="ntl-matrix-swatch ntl-cell-none" /> ללא שימוש
              </span>
              <span className="ntl-matrix-legend-item">
                <span className="ntl-matrix-swatch ntl-cell-idle" /> מחובר, ללא שימוש
              </span>
              <span className="ntl-matrix-legend-item">
                <span className="ntl-matrix-swatch ntl-cell-low" /> שימוש נמוך
              </span>
              <span className="ntl-matrix-legend-item">
                <span className="ntl-matrix-swatch ntl-cell-high" /> שימוש גבוה
              </span>
            </div>
            <div className="ntl-scroll-x">
              <table className="ntl-matrix">
                <thead>
                  <tr>
                    <th className="ntl-matrix-clinic-col">קליניקה</th>
                    {FEATURE_KEYS.map((key) => (
                      <th key={key}>{FEATURE_LABELS[key]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((row) => (
                    <tr key={row.id || row.clinicId}>
                      <td className="ntl-matrix-clinic-col">
                        <button
                          type="button"
                          className="ntl-link-btn"
                          onClick={() => goToClinic(row.clinicId)}
                        >
                          {row.clinicName || "ללא שם"}
                        </button>
                      </td>
                      {FEATURE_KEYS.map((key) => {
                        const count = row?.features30?.[key] || 0;
                        const st = row?.featureStatus?.[key];
                        const intensity = cellIntensity(count, st?.state);
                        const title = st
                          ? `${FEATURE_LABELS[key]}: ${featureStateLabel(st.state)}${
                              st.source === "server" ? " (מהשרת)" : ""
                            }`
                          : FEATURE_LABELS[key];
                        return (
                          <td key={key}>
                            <span className={`ntl-matrix-cell ntl-cell-${intensity}`} title={title}>
                              {intensity === "idle" ? <Plug size={11} weight="bold" /> : count > 0 ? count : ""}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Clinic score table */}
      <section className="ntl-card">
        <h3 className="ntl-card-title">
          <ChartLineUp size={15} />
          ציוני קליניקות
        </h3>
        {sortedScores.length === 0 ? (
          <div className="ntl-empty">אין עדיין נתוני ציונים</div>
        ) : (
          <div className="ntl-scroll-x">
            <table className="ntl-table">
              <thead>
                <tr>
                  {USAGE_COLUMNS.map((col) => (
                    <th key={col.key}>
                      <button
                        type="button"
                        className={`ntl-sort-btn${sort.key === col.key ? " active" : ""}`}
                        onClick={() => toggleSort(col.key)}
                      >
                        {col.label}
                        {sort.key === col.key &&
                          (sort.dir === "desc" ? (
                            <CaretDown size={10} weight="fill" />
                          ) : (
                            <CaretUp size={10} weight="fill" />
                          ))}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedScores.map((row) => (
                  <tr
                    key={row.id || row.clinicId}
                    className="ntl-table-row"
                    onClick={() => goToClinic(row.clinicId)}
                  >
                    <td className="ntl-table-name">{row.clinicName || "ללא שם"}</td>
                    <td>
                      <span
                        className={`ntl-score-badge ntl-score-${loginTone(
                          row.daysSinceLastLogin
                        )}`}
                      >
                        {row.daysSinceLastLogin == null
                          ? "מעולם"
                          : `${Number(row.daysSinceLastLogin)} ימים`}
                      </span>
                    </td>
                    <td>{Number(row.sessions30) || 0}</td>
                    <td>{Number(row.meetingsPast30) || 0}</td>
                    <td>{Number(row.meetingsCompleted30) || 0}</td>
                    <td>
                      <span
                        className={`ntl-score-badge ntl-score-${
                          Number(row.meetingsNext7) > 0 ? "ok" : "danger"
                        }`}
                        title="פגישות מתוכננות בשבוע הקרוב"
                      >
                        {Number(row.meetingsNext7) || 0}
                      </span>
                    </td>
                    <td>{Number(row.integrations) || 0}</td>
                    <td>
                      {row.pendingUpdateCount == null ? (
                        "—"
                      ) : (
                        <span
                          className={`ntl-score-badge ntl-score-${pendingUpdateTone(row.pendingUpdateCount)}`}
                          title="פגישות שהסתיימו ב-30 הימים האחרונים וממתינות לאישור / סיכום / תשלום"
                        >
                          {Number(row.pendingUpdateCount) || 0}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}

export default NivTelemetryTab;
